// Typed client for the mbank-sync edge function.
//
// Wraps the admin actions the /bank/inbox UI invokes:
//   cancel_batch, cancel_batch_item, manual_match, send_batches,
//   prepare_payments, poll_outbound.
//
// All methods either return a typed success payload or throw.

import { BaseEdgeFnClient } from './BaseEdgeFnClient.ts';
import type {
  MbankFxRateSource,
  MbankPaymentLinkType,
} from '@shared/types/mbank.ts';
import type { CanonicalizationCounts } from '../mbank/BankAccountCanonicalizationService.ts';
import type { DuplicatePurgeCounts } from '../mbank/BankAccountDuplicatePurgeService.ts';
import type { BackfillCounts } from '../mbank/BankAccountTrustBackfill.ts';
import type { VatAccountBackfillCounts } from '../mbank/VatAccountBackfill.ts';
import type { OverlinkedInvoiceRepairReport } from '../mbank/OverlinkedInvoiceRepairService.ts';
import type { PaymentMatchRepairReport } from '../mbank/PaymentMatchRepairService.ts';
import type { PaymentLedgerBackfillResult } from '../payments/PaymentLedgerBackfill.ts';

// Local alias for readability; matches MbankPaymentLinkType.
export type BankManualMatchLinkType = MbankPaymentLinkType;

// ── Payloads ─────────────────────────────────────────────────────────────

export interface BankCancelBatchResult {
  batchId: number;
  status: string;
}

export interface BankCancelBatchItemResult {
  cancelled: 'item' | 'batch';
  batchId: number;
  itemCount: number;
  totalAmount: number;
  status: string;
}

export interface BankManualMatchSplit {
  invoiceId: number;
  appliedAmount: number;
  invoiceCurrency: string;
  fxRate: number;
  fxRateSource: MbankFxRateSource;
  fxRateDate: string;
  linkType?: BankManualMatchLinkType;
  note?: string;
}

export interface BankManualMatchResult {
  statementLineId: number;
  splitsApplied: number;
  totalApplied: number;
  lineAmount: number;
  fullyCovered: boolean;
  newMatchState: 'matched' | 'partially_matched';
  createdLinkIds: number[];
}

export interface BankUnlinkPaymentResult {
  linkId: number;
  statementLineId: number;
  remainingLinkCount: number;
  newMatchState: 'pending' | 'matched' | 'partially_matched';
}

export interface BankRetryFakturowniaPushResult {
  resetCount: number;
  scope: 'single' | 'all';
  linkId?: number;
}

export interface BankRetryDeniedBatchResult {
  originalBatchId: number;
  newBatchId: number;
  clientOrderGroupId: string;
  itemCount: number;
  totalAmount: number;
}

export interface BankSendBatchesResult {
  attempted: number;
  sent: number;
  failed: number;
}

export interface BankPrepareResult {
  eligibleCount: number;
  blockedInFlightCount: number;
  skippedNoIbanCount: number;
  skippedZeroRemainderCount: number;
  batchesCreated: number;
  itemsCreated: number;
  groupKeys: string[];
  skippedNoDueDateCount: number;
  skippedOutsideWindowCount: number;
  dueDateWindowDays?: number;
  dueDateWindowCutoff?: string;
}

/** Result of the operator-driven "Do zapłaty" prepare_payments_selected
 *  action — pinned contract shared with the FE (camt-payment-file spec). */
export interface BankPrepareSelectedResult {
  batches: { id: number; currency: string; itemCount: number; totalAmount: number }[];
  skipped: { invoiceId: number; invoiceNumber?: string; code: string; message: string }[];
  itemsCreated: number;
}

export interface BankSendSingleBatchResult {
  batchId: number;
  status: string;
  asyncTaskId?: string;
}

export interface BankIssuePaymentResult {
  mode: 'prepared' | 'sent';
  batchId: number;
  clientOrderGroupId: string;
  itemCount: number;
  totalAmount: number;
  status: string;
  asyncTaskId?: string;
}

export interface BankMarkSentManuallyResult {
  batchId: number;
  status: string;
  sentManually: boolean;
  sentAt: string | null;
}

export interface BankIngestStatementManualResult {
  fileName: string;
  parsed: {
    accountIban: string;
    statementDate: string;
    entryCount: number;
    /** Number of statements in the file (always 1 for camt.053). */
    statementCount?: number;
  };
  result: {
    /** Set on single-statement ingests (camt.053). Omitted when aggregated. */
    statementId?: number;
    /** Set on single-statement ingests (camt.053). Omitted when aggregated. */
    wasInsertedStatement?: boolean;
    linesProcessed: number;
    linesCreated: number;
    linesSeenAgain: number;
    linksCreated: number;
    unmatchedCount: number;
    ambiguousCount: number;
    batchesSettled: number;
  };
}

// Legacy alias kept so older imports don't need to be touched now that
// MT940 shares the same result shape.
export type BankIngestCamt053ManualResult = BankIngestStatementManualResult;

export interface BankRematchPendingResult {
  linesProcessed: number;
  linksCreated: number;
  stillPending: number;
  nowAmbiguous: number;
  /** Keyset cursor for the next batch; null/absent when the pending set is exhausted. */
  next?: { bookingDate: string; id: number } | null;
}

// ── Client ───────────────────────────────────────────────────────────────

export class BankEdgeFnClient extends BaseEdgeFnClient {
  private static readonly FN = 'mbank-sync';

  cancelBatch(batchId: number): Promise<BankCancelBatchResult> {
    return this.callOrThrow<BankCancelBatchResult>(BankEdgeFnClient.FN, {
      action: 'cancel_batch',
      batchId,
    });
  }

  cancelBatchItem(batchId: number, itemId: number): Promise<BankCancelBatchItemResult> {
    return this.callOrThrow<BankCancelBatchItemResult>(BankEdgeFnClient.FN, {
      action: 'cancel_batch_item',
      batchId,
      itemId,
    });
  }

  manualMatch(
    statementLineId: number,
    splits: BankManualMatchSplit[],
  ): Promise<BankManualMatchResult> {
    return this.callOrThrow<BankManualMatchResult>(BankEdgeFnClient.FN, {
      action: 'manual_match',
      statementLineId,
      splits,
    });
  }

  sendBatches(limit?: number): Promise<BankSendBatchesResult> {
    return this.callOrThrow<BankSendBatchesResult>(BankEdgeFnClient.FN, {
      action: 'send_batches',
      limit,
    });
  }

  preparePayments(): Promise<BankPrepareResult> {
    return this.callOrThrow<BankPrepareResult>(BankEdgeFnClient.FN, {
      action: 'prepare_payments',
    });
  }

  /**
   * Operator-driven "Do zapłaty" flow: build pending_send batches from
   * hand-picked payable invoices. Re-validates server-side (assessPayable
   * against fresh data) immediately before staging — the FE's own
   * assessment only gates which rows are selectable, it is never trusted
   * for what actually gets paid. Returns created batches plus a per-invoice
   * skipped report so the FE can show why any row didn't generate.
   */
  preparePaymentsSelected(
    invoiceIds: number[],
    opts?: { requestedExecutionDate?: string },
  ): Promise<BankPrepareSelectedResult> {
    return this.callOrThrow<BankPrepareSelectedResult>(BankEdgeFnClient.FN, {
      action: 'prepare_payments_selected',
      invoiceIds,
      requestedExecutionDate: opts?.requestedExecutionDate,
    });
  }

  pollOutbound(): Promise<{ polled: number; updated: number }> {
    return this.callOrThrow<{ polled: number; updated: number }>(BankEdgeFnClient.FN, {
      action: 'poll_outbound',
    });
  }

  /** Manual-bridge: record that an operator delivered the batch via CompanyNet UI. */
  markBatchSentManually(batchId: number): Promise<BankMarkSentManuallyResult> {
    return this.callOrThrow<BankMarkSentManuallyResult>(BankEdgeFnClient.FN, {
      action: 'mark_batch_sent_manually',
      batchId,
    });
  }

  /**
   * Reset one failed fakturownia push to 'pending' (or all failed when
   * linkId omitted). The next /push-payments cron will retry it.
   */
  retryFakturowniaPush(
    linkId?: number,
  ): Promise<BankRetryFakturowniaPushResult> {
    return this.callOrThrow<BankRetryFakturowniaPushResult>(BankEdgeFnClient.FN, {
      action: 'retry_fakturownia_push',
      linkId,
    });
  }

  /** Create a fresh pending_send batch from the items of a denied one. */
  retryDeniedBatch(batchId: number): Promise<BankRetryDeniedBatchResult> {
    return this.callOrThrow<BankRetryDeniedBatchResult>(BankEdgeFnClient.FN, {
      action: 'retry_denied_batch',
      batchId,
    });
  }

  /** Delete one mbank_payment_links row; line match_state is recomputed. */
  unlinkPayment(linkId: number): Promise<BankUnlinkPaymentResult> {
    return this.callOrThrow<BankUnlinkPaymentResult>(BankEdgeFnClient.FN, {
      action: 'unlink_payment',
      linkId,
    });
  }

  /** Manual-bridge: ingest a camt.053 file the operator downloaded from CompanyNet. */
  ingestCamt053Manual(
    fileContent: string,
    fileName?: string,
  ): Promise<BankIngestStatementManualResult> {
    return this.callOrThrow<BankIngestStatementManualResult>(BankEdgeFnClient.FN, {
      action: 'ingest_camt053_manual',
      fileContent,
      fileName,
    });
  }

  /** Manual-bridge: ingest an MT940 file the operator downloaded from CompanyNet. */
  ingestMt940Manual(
    fileContent: string,
    fileName?: string,
  ): Promise<BankIngestStatementManualResult> {
    return this.callOrThrow<BankIngestStatementManualResult>(BankEdgeFnClient.FN, {
      action: 'ingest_mt940_manual',
      fileContent,
      fileName,
    });
  }

  /**
   * Re-run the matcher against the current invoice set for every
   * still-pending statement line. Used after the operator added the
   * missing invoices in Fakturownia and wants previously-unmatched
   * bank entries to resolve without re-importing the source file.
   */
  rematchPending(
    opts?: { after?: { bookingDate: string; id: number } | null; limit?: number },
  ): Promise<BankRematchPendingResult> {
    return this.callOrThrow<BankRematchPendingResult>(BankEdgeFnClient.FN, {
      action: 'rematch_pending',
      ...(opts?.after != null ? { after: opts.after } : {}),
      ...(opts?.limit != null ? { limit: opts.limit } : {}),
    });
  }

  /**
   * Send a single pending_send batch to mBank. Only touches the
   * specified batch — never affects other batches.
   */
  sendBatch(batchId: number): Promise<BankSendSingleBatchResult> {
    return this.callOrThrow<BankSendSingleBatchResult>(BankEdgeFnClient.FN, {
      action: 'send_batch',
      batchId,
    });
  }

  /**
   * Admin-only maintenance: normalize/dedupe pass over company_bank_accounts
   * (canonical account format + twin-group merge, then deletion of the
   * merged-away twins). Idempotent — a re-run returns all-zero counts. Run
   * BEFORE backfillAccountTrust so the backfill sees merged rows.
   */
  canonicalizeBankAccounts(): Promise<CanonicalizationCounts & DuplicatePurgeCounts> {
    return this.callOrThrow<CanonicalizationCounts & DuplicatePurgeCounts>(BankEdgeFnClient.FN, {
      action: 'canonicalize_bank_accounts',
    });
  }

  /**
   * Admin-only maintenance: re-runnable backfill of the bank-account trust
   * ladder from mbank statement-line evidence.
   */
  backfillAccountTrust(): Promise<BackfillCounts> {
    return this.callOrThrow<BackfillCounts>(BankEdgeFnClient.FN, {
      action: 'backfill_account_trust',
    });
  }

  /**
   * Admin-only maintenance: re-runnable backfill flagging each company's
   * separate PLN VAT-part account (is_vat_account), derived from historical
   * EUR-invoice payment evidence.
   */
  backfillVatAccounts(): Promise<VatAccountBackfillCounts> {
    return this.callOrThrow<VatAccountBackfillCounts>(BankEdgeFnClient.FN, {
      action: 'backfill_vat_accounts',
    });
  }

  /**
   * Admin-only maintenance: re-runnable wipe+rebuild of the v2 payment ledger
   * (payments / payment_allocations / invoice_holds) plus the app-layer
   * shadow-status projection and the old-vs-v2 diff report — the P1
   * acceptance gate of the payment-module redesign. Safe to re-run anytime;
   * nothing in production reads v2 state until the P2 swap.
   */
  backfillPaymentsLedger(): Promise<PaymentLedgerBackfillResult> {
    return this.callOrThrow<PaymentLedgerBackfillResult>(BankEdgeFnClient.FN, {
      action: 'backfill_payments_ledger',
    });
  }

  /**
   * Admin-only maintenance: delete the automatic bank links of invoices linked
   * beyond their effective gross, return the freed statement lines to the
   * matcher and re-open the Fakturownia payments allocated to a deleted link.
   * Manual links are kept. Safe to re-run.
   */
  repairOverlinkedInvoices(): Promise<OverlinkedInvoiceRepairReport> {
    return this.callOrThrow<OverlinkedInvoiceRepairReport>(BankEdgeFnClient.FN, {
      action: 'repair_overlinked_invoices',
    });
  }

  /**
   * Admin-only maintenance: remove MT940/camt053 duplicate statement lines and
   * the automatic amount-only links that contradict their line's title, sending
   * those lines back to the matcher. Manual links are kept. Safe to re-run.
   */
  repairPaymentMatching(): Promise<PaymentMatchRepairReport> {
    return this.callOrThrow<PaymentMatchRepairReport>(BankEdgeFnClient.FN, {
      action: 'repair_payment_matching',
    });
  }

  /**
   * Issue a one-off payment for a single cost invoice via the
   * mbank-send-payment edge function. The invoice must be approved
   * and unpaid/partial.
   */
  issuePayment(
    invoiceId: number,
    options?: { requestedExecutionDate?: string; sendImmediately?: boolean },
  ): Promise<BankIssuePaymentResult> {
    return this.callOrThrow<BankIssuePaymentResult>('mbank-send-payment', {
      invoiceId,
      requestedExecutionDate: options?.requestedExecutionDate,
      sendImmediately: options?.sendImmediately,
    });
  }
}

